import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getTariffe, saveTariffa, deleteTariffa } from '../services/firebase';
import Topbar from '../components/layout/Topbar';
import { Panel, PanelHead, PanelBody, Button, Spinner } from '../components/ui';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

// Righe in cui il servizio è effettivamente presente nel layoutTemplate
function getRigheServizio(servizioId, layoutTemplate, gridRows) {
  const righe = [...new Set(
    layoutTemplate
      .filter(c => c.type === servizioId)
      .map(c => c.row)
  )].sort((a, b) => a - b);
  return righe.length > 0 ? righe : Array.from({ length: gridRows }, (_, i) => i);
}

export default function Tariffe() {
  const { beach, admin, tariffe: tariffeCached, refreshBeach } = useAuth();
  const [tariffe, setTariffe] = useState(tariffeCached || []);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const servizi = beach?.servizi || [];
  const gridRows = beach?.gridRows || 5;
  const layoutTemplate = beach?.layoutTemplate || [];

  useEffect(() => { setTariffe(tariffeCached); }, [tariffeCached]);

  const handleSave = async (tariffa) => {
    setLoading(true);
    try {
      await saveTariffa(admin.beachCode, tariffa);
      await refreshBeach();
      setEditing(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa fascia tariffaria?')) return;
    await deleteTariffa(admin.beachCode, id);
    await refreshBeach();
  };

  const getSovrapposizioni = (t) =>
    tariffe.filter(other =>
      other.id !== t.id && other.dal <= t.al && other.al >= t.dal
    );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Topbar
        title="Tariffe stagionali"
        subtitle="Gestisci i prezzi per periodo"
        actions={
          <Button variant="primary" onClick={() => setEditing({})}>
            + Nuova fascia
          </Button>
        }
      />

      <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1 }}>
        {editing !== null && (
          <TariffaForm
            tariffa={editing}
            servizi={servizi}
            gridRows={gridRows}
            layoutTemplate={layoutTemplate}
            onSave={handleSave}
            onCancel={() => setEditing(null)}
            loading={loading}
          />
        )}

        {tariffe.length === 0 && editing === null ? (
          <Panel>
            <PanelBody>
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                <div style={{ fontSize: '28px', marginBottom: '10px' }}>💰</div>
                <div style={{ fontSize: '13px', marginBottom: '16px' }}>
                  Nessuna fascia tariffaria configurata.<br />
                  Crea la tua prima tariffa stagionale.
                </div>
                <Button variant="primary" onClick={() => setEditing({})}>
                  + Crea prima tariffa
                </Button>
              </div>
            </PanelBody>
          </Panel>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {tariffe.map((t) => (
              <TariffaCard
                key={t.id}
                tariffa={t}
                servizi={servizi}
                gridRows={gridRows}
                layoutTemplate={layoutTemplate}
                sovrapposizioni={getSovrapposizioni(t).length}
                onEdit={() => setEditing(t)}
                onDelete={() => handleDelete(t.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── TariffaCard ─────────────────────────────────────────────────────────────

function TariffaCard({ tariffa, servizi, gridRows, layoutTemplate, sovrapposizioni, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const today = new Date().toISOString().split('T')[0];
  const isActive = tariffa.dal <= today && tariffa.al >= today;
  const isFutura = tariffa.dal > today;

  return (
    <Panel>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{ padding: '16px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '14px' }}
      >
        <div style={{
          width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0,
          background: isActive ? '#4ade80' : isFutura ? 'var(--sand)' : 'var(--text-muted)',
        }} />

        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '14px', fontWeight: 500 }}>{tariffa.nome}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
            {tariffa.dal} → {tariffa.al}
            {isActive && <span style={{ color: '#4ade80', marginLeft: '8px' }}>● Attiva</span>}
            {isFutura && <span style={{ color: 'var(--sand)', marginLeft: '8px' }}>● Futura</span>}
          </div>
        </div>

        {sovrapposizioni > 0 && (
          <div style={{
            padding: '3px 10px', borderRadius: '20px', fontSize: '11px',
            background: 'rgba(245,200,66,0.15)', color: 'var(--sand)',
            border: '1px solid rgba(245,200,66,0.3)',
          }}>
            ⚠ {sovrapposizioni} sovrapposiz.
          </div>
        )}

        <div style={{ display: 'flex', gap: '6px' }}>
          <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); onEdit(); }}>
            Modifica
          </Button>
          <Button size="sm" variant="danger" onClick={e => { e.stopPropagation(); onDelete(); }}>
            Elimina
          </Button>
        </div>

        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
          style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', color: 'var(--text-muted)', flexShrink: 0 }}>
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      {expanded && (
        <div style={{ padding: '0 20px 16px', borderTop: '1px solid var(--border)' }}>
          <PrezziTable tariffa={tariffa} servizi={servizi} gridRows={gridRows} layoutTemplate={layoutTemplate} />
        </div>
      )}
    </Panel>
  );
}

// ─── PrezziTable ─────────────────────────────────────────────────────────────

function PrezziTable({ tariffa, servizi, gridRows, layoutTemplate }) {
  return (
    <div style={{ marginTop: '14px', overflowX: 'auto' }}>
      <table style={{ width: '100%', fontSize: '12.5px', borderCollapse: 'collapse' }}>
        <tbody>
          {servizi.map(s => {
            const righe = getRigheServizio(s.id, layoutTemplate, gridRows);
            return (
              <tr key={s.id}>
                <td style={{ padding: '8px 12px 8px 0', color: 'var(--text-soft)', whiteSpace: 'nowrap' }}>
                  <span style={{ marginRight: '6px' }}>{s.icona}</span>{s.label}
                </td>
                {s.haFila ? (
                  righe.map((rowIndex) => {
                    const key = `${s.id}_r${rowIndex}`;
                    const val = tariffa.prezzi?.[key];
                    return (
                      <td key={rowIndex} style={{ padding: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '2px' }}>
                          Fila {LETTERS[rowIndex]}
                        </div>
                        <div style={{ color: val ? 'var(--sand)' : 'var(--text-muted)' }}>
                          {val ? `€ ${val}` : '—'}
                        </div>
                      </td>
                    );
                  })
                ) : (
                  <td style={{ padding: '8px', color: tariffa.prezzi?.[s.id] ? 'var(--sand)' : 'var(--text-muted)' }}>
                    {tariffa.prezzi?.[s.id] ? `€ ${tariffa.prezzi[s.id]}` : '—'}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── TariffaForm ─────────────────────────────────────────────────────────────

function TariffaForm({ tariffa, servizi, gridRows, layoutTemplate, onSave, onCancel, loading }) {
  const [form, setForm] = useState({
    nome: tariffa.nome || '',
    dal: tariffa.dal || '',
    al: tariffa.al || '',
    prezzi: tariffa.prezzi || {},
    ...(tariffa.id ? { id: tariffa.id } : {}),
  });

  const setPrezzo = (key, val) =>
    setForm(p => ({ ...p, prezzi: { ...p.prezzi, [key]: Number(val) || 0 } }));

  const inputStyle = {
    background: 'var(--surface)', border: '1px solid var(--border-mid)',
    borderRadius: 'var(--radius-md)', color: '#fff',
    padding: '8px 12px', fontSize: '13px', fontFamily: 'var(--font-sans)', width: '100%',
  };

  const priceInput = (key) => (
    <div style={{ position: 'relative' }}>
      <span style={{ position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: 'var(--sand)' }}>€</span>
      <input
        type="number" min="0" step="0.5"
        value={form.prezzi[key] || ''}
        onChange={e => setPrezzo(key, e.target.value)}
        style={{ ...inputStyle, paddingLeft: '22px', width: '90px', textAlign: 'right' }}
      />
    </div>
  );

  return (
    <div style={{
      background: 'var(--navy-mid)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: '24px', marginBottom: '20px',
    }}>
      <div style={{ fontSize: '15px', fontWeight: 500, marginBottom: '20px' }}>
        {tariffa.id ? 'Modifica fascia tariffaria' : 'Nuova fascia tariffaria'}
      </div>

      {/* Campi base — rimossa la priorità, vince automaticamente la fascia più corta */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', marginBottom: '24px' }}>
        <div>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Nome fascia</label>
          <input placeholder="es. Alta stagione, Ferragosto..."
            value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value }))}
            style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Dal</label>
          <input type="date" value={form.dal}
            onChange={e => setForm(p => ({ ...p, dal: e.target.value }))} style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Al</label>
          <input type="date" value={form.al}
            onChange={e => setForm(p => ({ ...p, al: e.target.value }))} style={inputStyle} />
        </div>
      </div>

      {/* Nota priorità automatica */}
      <div style={{
        background: 'rgba(31,147,224,0.08)', border: '1px solid rgba(31,147,224,0.2)',
        borderRadius: 'var(--radius-md)', padding: '10px 14px',
        fontSize: '12px', color: 'var(--text-muted)', marginBottom: '20px',
      }}>
        💡 In caso di date sovrapposte, vince automaticamente la fascia con il <strong style={{ color: 'var(--text-soft)' }}>range più corto</strong> (più specifica).
      </div>

      <div style={{ fontSize: '12px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>
        Prezzi al giorno
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '20px' }}>
        {servizi.map(s => {
          const righe = getRigheServizio(s.id, layoutTemplate, gridRows);
          return (
            <div key={s.id} style={{
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)', padding: '14px 16px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: s.haFila ? '12px' : '8px' }}>
                <span style={{ fontSize: '16px' }}>{s.icona}</span>
                <span style={{ fontSize: '13.5px', fontWeight: 500 }}>{s.label}</span>
                {s.haFila && (
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    — prezzo per fila (riga {LETTERS[righe[0]]} = fronte mare)
                  </span>
                )}
              </div>

              {s.haFila ? (
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {righe.map((rowIndex) => (
                    <div key={rowIndex}>
                      <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                        Fila {LETTERS[rowIndex]}{rowIndex === righe[0] ? ' (fronte mare)' : ''}
                      </label>
                      {priceInput(`${s.id}_r${rowIndex}`)}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Prezzo unico:</label>
                  {priceInput(s.id)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {servizi.length === 0 && (
        <div style={{ fontSize: '13px', color: 'var(--text-muted)', padding: '16px', textAlign: 'center', marginBottom: '20px' }}>
          Configura prima i servizi nel Beach Builder
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px' }}>
        <Button variant="primary"
          onClick={() => onSave(form)}
          disabled={loading || !form.nome || !form.dal || !form.al}>
          {loading ? <Spinner size={14} /> : tariffa.id ? 'Salva modifiche' : 'Crea fascia'}
        </Button>
        <Button variant="ghost" onClick={onCancel}>Annulla</Button>
      </div>
    </div>
  );
}